import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import Select from "react-select";
import { saveAs } from "file-saver";
import RotateDevice from "../../components/RotateDevice/RotateDevice";
import Lf23Loader from "../../components/Lf23Loader";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import ModaleStoricoTratta from "./ModaleStoricoTratta";
import VistaMappaRecuperi, { MappaCarrello, useGeometrieRecuperi } from "./MappaRecuperi";
import {
    API_URL, authHeaders, nf, km, quartiere, formatData,
    FullscreenBtn, ExportBtn, AvvisoEsecuzioni, KpiCard, ico, FiltroMulti, valoriDi, Stella, clicFuori,
} from "./comuni";

/**
 * Tab "Tratte da recuperare".
 *
 * Le tratte previste in un percorso e non eseguite restano in sospeso: una mancata
 * accende la stella gialla, due consecutive la rossa. Da qui si compone il giro di
 * recupero — si raccolgono le tratte col "+", il contatore apre un modale in cui si
 * scelgono data e operatori e si mette in ordine il giro.
 *
 * E' uno STATO CORRENTE, non una statistica di periodo: per questo non ha i filtri
 * mese/settimana della Reportistica e vive in un tab suo.
 *
 * Sorgente: GET /api/gestione-percorsi/recuperi, che calcola le mancate consecutive
 * sullo storico dei percorsi conclusi (tratteRecuperoService) e le arricchisce con
 * via, tratto, percorsi che le servono, passaggi/anno e lunghezza.
 *
 * Vista Tabella o Mappa (task 548): sulla mappa le tratte hanno il colore della stella e si
 * aggiungono allo STESSO carrello della tabella (perno), col dettaglio nel pannello accanto.
 *
 * "Crea percorso" scrive il giro su Interacta (POST /percorsi/recupero) con il nome
 * "RECn ..." (progressivo del giorno nella zona). Il backend salva anche quale passaggio
 * saltato recupera ogni tratta: e' cosi' che la Reportistica le riconta nel giorno giusto.
 */

/* TODO(461): valore DA CONFERMARE con Matteo. Nella call era stato citato un minimo di
   chilometri sotto il quale un percorso di recupero non sta in piedi, ma il numero non e'
   nei punti che ci sono arrivati e non esiste da nessuna parte nel repo. Tenuto tondo e
   basso di proposito: le tratte da recuperare sono corte (poche centinaia di metri l'una),
   quindi con una soglia alta l'avviso resterebbe sempre acceso e non direbbe piu' niente. */
const MIN_KM_RECUPERO = 2;

/* La community 122 ha 50 slot "Strada": oltre, il backend rifiuta. Il limite vive
   anche li' (MAX_SLOT_STRADA) ed e' li' che fa fede; qui serve solo a spegnere il
   bottone e a dirlo prima, invece di far scoprire il muro dopo il salvataggio. */
const MAX_TRATTE_PERCORSO = 50;

// Nome precompilato del percorso di recupero, come chiesto in revisione.
const NOME_RECUPERO = "Recupero";

/* Data proposta per il recupero: domani. Si puo' scegliere anche OGGI: e' il caso del
   giro della mattina chiuso a meta' e completato nel pomeriggio da un'altra squadra.
   In ISO, che e' il formato di DatePickerModal, con la data LOCALE (toISOString darebbe
   il giorno UTC, sbagliato dopo mezzanotte). */
const isoLocale = (scarto = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + scarto);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const oggi = isoLocale(0);
const domani = isoLocale(1);

/** Sposta un elemento da una posizione all'altra. Stessa implementazione di
    moveInArray in TabAnagraficaPercorsi: l'ordine delle tratte e' l'ordine del giro. */
const moveInArray = (arr, from, to) => {
    if (from == null || to == null || from === to || from < 0 || to < 0) return arr;
    if (from >= arr.length || to >= arr.length) return arr;
    const next = [...arr];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
};

/* Stella delle tratte da recuperare: e' la `Stella` condivisa di comuni.js (stessa
   dei tab Percorsi e del modale storico). Usata in tabella e nel modale di recupero. */
const stellaRecupero = (c) => <Stella livello={c.livello} volte={c.volte} />;

/* Colonne ordinabili della tabella: per ognuna il valore su cui si confrontano le
   righe e il verso del primo clic (numeri e urgenza dal piu' grande, testi dalla A,
   date dalla piu' vecchia: chi aspetta da piu' tempo). Lo stato e' una coppia
   [colore, mancate]: la soglia del rosso e' configurabile, quindi il colore non si
   ricava dalle mancate e va confrontato prima. */
const RANGO_LIVELLO = { rossa: 2, gialla: 1 };
const COLONNE_ORDINABILI = {
    stato:     { valore: (c) => [RANGO_LIVELLO[c.livello] || 0, c.volte || 0], primo: "desc" },
    via:       { valore: (c) => c.via || "", primo: "asc" },
    tratto:    { valore: (c) => c.tratto || "", primo: "asc" },
    percorsi:  { valore: (c) => (c.percorsi || []).join(", "), primo: "asc" },
    quartiere: { valore: (c) => quartiere(c.quartiere), primo: "asc" },
    pas:       { valore: (c) => c.pas ?? 0, primo: "desc" },
    metri:     { valore: (c) => c.metri ?? 0, primo: "desc" },
    // le date sono ISO: l'ordine alfabetico e' gia' quello cronologico
    ultima:    { valore: (c) => c.ultima || "", primo: "asc" },
};

/* Confronto di due valori di COLONNE_ORDINABILI: le coppie elemento per elemento,
   i numeri per differenza, i testi alla italiana (accenti e maiuscole non contano). */
const confronta = (a, b) => {
    if (Array.isArray(a)) {
        for (let i = 0; i < a.length; i++) {
            const d = confronta(a[i], b[i]);
            if (d) return d;
        }
        return 0;
    }
    if (typeof a === "number") return a - b;
    return String(a).localeCompare(String(b), "it", { numeric: true, sensitivity: "base" });
};

/* Intestazione cliccabile: ordina sulla colonna `k` e mostra la freccia del verso. */
const ThOrdinabile = ({ k, sort, onSort, className, title, children }) => (
    <th className={className} style={{ cursor: "pointer" }} onClick={() => onSort(k)} title={title}>
        {children}{sort.key === k ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
    </th>
);

/**
 * Il corpo della pagina: riceve i dati gia' caricati dal guscio in fondo al file.
 */
const Recuperi = ({ dati, onAggiorna, aggiornando, zona }) => {
    const { elenco, totali, rosse, meta } = dati;

    // I quartieri disponibili escono dai dati stessi: finche' non sono sull'anagrafica
    // 120 questo elenco resta vuoto e i due filtri per quartiere non compaiono.
    const quartieri = useMemo(
        () => [...new Set(elenco.map((c) => c.quartiere).filter(Boolean))].sort(),
        [elenco]
    );
    const conQuartieri = quartieri.length > 0;

    const [fsRecuperi, setFsRecuperi] = useState(false);
    // Tabella o Mappa: due modi di guardare (e mettere nel carrello) le stesse righe filtrate.
    const [vista, setVista] = useState("tabella");

    /* ------------------------------- filtri -------------------------------
       Pannello a scomparsa come nelle altre pagine: la bozza si applica solo su
       "Applica", cosi' cambiando tre tendine la tabella non si ridisegna tre volte
       (qui le righe sono centinaia). */
    const [showFilters, setShowFilters] = useState(false);
    const VUOTO = { via: "", quartieri: [], percorsi: [], livelli: [], pas: [] };
    const [query, setQuery] = useState(VUOTO);
    // Bozza del pannello: react-select lavora con {value,label}, la query con i soli valori.
    const [fVia, setFVia] = useState("");
    const [fQuartieri, setFQuartieri] = useState([]);
    const [fPercorsi, setFPercorsi] = useState([]);
    const [fLivelli, setFLivelli] = useState([]);
    const [fPas, setFPas] = useState([]);

    // Le opzioni escono dai dati: niente elenchi scritti a mano che invecchiano.
    const opzioni = useMemo(() => ({
        quartieri: quartieri.map((q) => ({ value: q, label: quartiere(q) })),
        percorsi: [...new Set(elenco.flatMap((c) => c.percorsi || []))]
            .sort((a, b) => a.localeCompare(b, "it")).map((g) => ({ value: g, label: g })),
        livelli: [{ value: "rossa", label: "Rosse (2+ mancate)" }, { value: "gialla", label: "Gialle (1 mancata)" }],
        pas: [...new Set(elenco.map((c) => c.pas).filter((v) => v != null))]
            .sort((a, b) => a - b).map((n) => ({ value: String(n), label: `${n} passaggi/anno` })),
    }), [elenco, quartieri]);

    const applicaFiltri = () => setQuery({
        via: fVia, quartieri: valoriDi(fQuartieri), percorsi: valoriDi(fPercorsi),
        livelli: valoriDi(fLivelli), pas: valoriDi(fPas),
    });
    const resetFiltri = () => {
        setFVia(""); setFQuartieri([]); setFPercorsi([]); setFLivelli([]); setFPas([]);
        setQuery(VUOTO);
    };

    /* ---------------------------- ordinamento ----------------------------
       Di default l'elenco arriva gia' ordinato dal backend: prima le rosse, poi a
       parita' di livello chi ha piu' mancate e chi aspetta da piu' tempo. Cliccando
       un'intestazione si passa all'ordine di quella colonna (verso del primo clic in
       COLONNE_ORDINABILI); ricliccando si inverte, e la terza volta si torna
       all'ordine di default. */
    const [sort, setSort] = useState({ key: "", dir: "asc" });
    const toggleSort = (key) => setSort((s) => {
        const primo = COLONNE_ORDINABILI[key].primo;
        if (s.key !== key) return { key, dir: primo };
        if (s.dir === primo) return { key, dir: primo === "asc" ? "desc" : "asc" };
        return { key: "", dir: "asc" };   // terzo clic: si torna alla priorita' di recupero
    });
    const intestazione = { sort, onSort: toggleSort };

    /* ------------- carrello del percorso di recupero -------------
       Array e non Set: l'ordine con cui si aggiungono le tratte e' l'ordine del giro.
       Stesso idioma del picker tratti in TabAnagraficaPercorsi (addTratto/removeTratto). */
    const [carrello, setCarrello] = useState([]);
    /* Se dopo un aggiornamento una tratta non e' piu' nell'elenco vuol dire che e'
       stata eseguita: toglierla dal carrello evita di mandare qualcuno a rifare una
       cosa gia' fatta — che e' esattamente il motivo per cui si preme Aggiorna.
       Si riscrive il carrello solo se serve: `elenco` e' un array nuovo a ogni
       ricalcolo, e filtrarlo sempre farebbe un render in piu' per niente. */
    useEffect(() => {
        const vivi = new Set(elenco.map((c) => c.id));
        setCarrello((prec) => (prec.every((c) => vivi.has(c.id)) ? prec : prec.filter((c) => vivi.has(c.id))));
    }, [elenco]);
    const [modaleRecupero, setModaleRecupero] = useState(false);
    // Modale "Dettaglio": lo storico della tratta scelta (task 483). Null = chiuso.
    const [storicoTarget, setStoricoTarget] = useState(null);
    const [nomeRecupero, setNomeRecupero] = useState(NOME_RECUPERO);
    const [dataRecupero, setDataRecupero] = useState(domani);
    /* Creazione su Interacta: in corso, errore, e l'esito. `creato` tiene il codice del
       post fino alla chiusura del modale: far sparire tutto senza dire com'e' andata
       lascerebbe l'utente a chiedersi se ha funzionato. */
    const [creando, setCreando] = useState(false);
    const [erroreCrea, setErroreCrea] = useState(null);
    const [creato, setCreato] = useState(null);
    /* Progressivo del giorno (REC1, REC2...): piu' recuperi nella stessa data sono
       normali, il nome li distingue. Qui e' un'anteprima; il numero vero lo assegna il
       backend alla creazione, rileggendo Interacta. null = non ancora noto. */
    const [progressivo, setProgressivo] = useState(null);

    const inCarrello = useMemo(() => new Set(carrello.map((t) => t.id)), [carrello]);
    // La mappa ragiona per codice tratta: le linee blu sono queste.
    const codiciCarrello = useMemo(() => new Set(carrello.map((t) => t.codice)), [carrello]);
    // Geometrie delle mappe: si scaricano la prima volta che si apre la vista Mappa o il carrello.
    const geo = useGeometrieRecuperi(zona, dati, vista === "mappa" || modaleRecupero);
    const kmCarrello = useMemo(() => km(carrello.reduce((a, t) => a + t.metri, 0)), [carrello]);

    /* Riga appena aggiunta: resta evidenziata ~1s e poi il colore sfuma via.
       Stesso idioma di flashMoved/.gp-just-moved in TabAnagraficaPercorsi. */
    const [flashId, setFlashId] = useState(null);
    const flashTimer = useRef(null);
    useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

    const recuperiFiltrati = useMemo(() => {
        const via = query.via.trim().toLowerCase();
        // Dentro un filtro le scelte sono in OR (una qualsiasi va bene), fra filtri
        // diversi in AND: e' come funzionano le multi-select del resto del progetto.
        let righe = elenco.filter((c) =>
            (!via || `${c.via} ${c.tratto}`.toLowerCase().includes(via)) &&
            (!query.quartieri.length || query.quartieri.includes(c.quartiere)) &&
            (!query.percorsi.length || (c.percorsi || []).some((g) => query.percorsi.includes(g))) &&
            (!query.livelli.length || query.livelli.includes(c.livello)) &&
            (!query.pas.length || query.pas.includes(String(c.pas))));

        if (sort.key) {
            const segno = sort.dir === "asc" ? 1 : -1;
            const { valore } = COLONNE_ORDINABILI[sort.key];
            // sort stabile: a parita' di valore resta l'ordine di priorita' del backend
            righe = [...righe].sort((a, b) => segno * confronta(valore(a), valore(b)));
        }
        return righe;
    }, [elenco, query, sort]);

    // Quanti filtri sono attivi: lo dice il bottone, altrimenti col pannello chiuso
    // non si capisce perche' la tabella mostri meno righe del totale.
    const nFiltri = (query.via ? 1 : 0)
        + [query.quartieri, query.percorsi, query.livelli, query.pas].filter((a) => a.length).length;

    /* Operatori del percorso di recupero. Endpoint REALE (lo stesso del modale Genera
       in TabAnagraficaPercorsi), caricato pigramente all'apertura del modale e degradato
       a lista vuota in caso di errore: il resto del modale resta usabile. */
    const [utentiList, setUtentiList] = useState(null);
    const [utentiLoading, setUtentiLoading] = useState(false);
    const [operatori, setOperatori] = useState([]);
    const utentiOptions = useMemo(
        () => (utentiList || []).map((u) => ({ value: u.id, label: u.nome })),
        [utentiList]
    );
    const ensureUtenti = useCallback(async () => {
        if (utentiList) return;
        setUtentiLoading(true);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/utenti`, { headers: authHeaders() });
            setUtentiList(res.data?.data ?? []);
        } catch (err) {
            console.error("Errore nel caricamento degli operatori:", err);
            setUtentiList([]);
        } finally {
            setUtentiLoading(false);
        }
    }, [utentiList]);

    /* Riordino delle tratte nel carrello: stessi tre modi del modale Genera —
       maniglia drag su desktop, frecce su/giu' sotto i 1024px, numerino editabile. */
    const [dragIndex, setDragIndex] = useState(null);
    const [movedId, setMovedId] = useState(null);
    const movedTimerRef = useRef(null);
    const [idxEditId, setIdxEditId] = useState(null);
    const [idxEditVal, setIdxEditVal] = useState("");
    useEffect(() => () => { if (movedTimerRef.current) clearTimeout(movedTimerRef.current); }, []);

    const flashMoved = (id) => {
        setMovedId(id);
        if (movedTimerRef.current) clearTimeout(movedTimerRef.current);
        movedTimerRef.current = setTimeout(() => setMovedId(null), 1000);
    };
    // Il box dei selezionati ha scroll interno: una tratta che vola in cima finirebbe
    // fuori dalla parte visibile. rAF per aspettare che React abbia ridisegnato la lista.
    const scrollToTratta = (id) => {
        requestAnimationFrame(() => {
            document.querySelector(`[data-tratta-id="${id}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        });
    };
    const spostaTratta = (from, to) => setCarrello((arr) => moveInArray(arr, from, to));
    const applyIdxEdit = (from) => {
        const raw = idxEditVal;
        setIdxEditId(null);
        setIdxEditVal("");
        const n = parseInt(raw, 10);
        if (!Number.isFinite(n)) return;
        const to = Math.min(Math.max(n, 1), carrello.length) - 1;   // da 1-based a indice
        if (to === from) return;
        const id = carrello[from]?.id;
        spostaTratta(from, to);
        if (id != null) { flashMoved(id); scrollToTratta(id); }
    };

    const perno = (t) => {
        const dentro = carrello.some((x) => x.id === t.id);
        setCarrello((arr) => (dentro ? arr.filter((x) => x.id !== t.id) : [...arr, t]));
        if (dentro) return;                       // il lampeggio segnala l'aggiunta, non la rimozione
        setFlashId(t.id);
        if (flashTimer.current) clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlashId(null), 900);
    };
    const svuota = () => { setCarrello([]); setFlashId(null); };

    /* Filtro quartiere DENTRO il modale: guarda solo le tratte gia' inserite nel
       recupero, mentre quello in tabella guarda tutte. Sono due liste diverse,
       quindi due stati diversi. E' una vista: km totali e conteggi restano sul
       carrello intero. */
    const [qModale, setQModale] = useState("");
    const quartieriCarrello = useMemo(
        () => quartieri.filter((q) => carrello.some((c) => c.quartiere === q)),
        [quartieri, carrello]
    );
    /* Le righe da mostrare, con la posizione REALE nel giro: se filtro su Savena e
       la seconda tratta di Savena e' la 5a del percorso, deve leggersi 5, non 2. */
    const carrelloVisibile = useMemo(
        () => carrello.map((c, i) => ({ c, i })).filter(({ c }) => !qModale || c.quartiere === qModale),
        [carrello, qModale]
    );
    /* Linea toccata sulla mappa del modale: la sua riga si porta in vista e lampeggia,
       con lo stesso segnale del riordino. */
    const scegliDaMappa = (codice) => {
        const c = carrello.find((x) => x.codice === codice);
        if (!c) return;
        flashMoved(c.id);
        scrollToTratta(c.id);
    };
    // Col filtro attivo trascinare o usare le frecce sposterebbe rispetto a righe
    // non visibili: il riordino resta solo sulla lista intera.
    const riordinabile = !qModale;

    /* Anteprima del nome: si chiede al backend a ogni apertura del modale e a ogni cambio
       di data. Un errore lascia semplicemente l'anteprima vuota: il nome lo decide comunque
       il backend alla creazione. */
    useEffect(() => {
        if (!modaleRecupero || creato || !dataRecupero) return undefined;
        let annullato = false;
        axios.get(`${API_URL}/gestione-percorsi/percorsi/recupero/progressivo`, {
            headers: authHeaders(), params: { data: dataRecupero, zona },
        })
            .then((res) => { if (!annullato) setProgressivo(res.data?.progressivo ?? null); })
            .catch(() => { if (!annullato) setProgressivo(null); });
        return () => { annullato = true; };
    }, [modaleRecupero, creato, dataRecupero, zona]);

    const apriModale = () => { setModaleRecupero(true); ensureUtenti(); };
    const chiudiModale = () => {
        setModaleRecupero(false);
        setNomeRecupero(NOME_RECUPERO);
        setDataRecupero(domani);
        setOperatori([]);
        setQModale("");
        setErroreCrea(null);
        setProgressivo(null);
        /* Il carrello si svuota solo se il percorso e' stato davvero creato: se la
           chiamata e' fallita, chiudere per sbaglio non deve buttare via mezz'ora di
           selezione. L'elenco delle tratte da recuperare NON si ricarica, perche' non
           e' cambiato: il post nasce "Da Fare" e una tratta smette di essere da
           recuperare solo quando il giro viene eseguito. */
        if (creato) { setCarrello([]); setCreato(null); }
    };

    /* Perche' il bottone e' spento. Dirlo nel title evita il classico "non funziona":
       i vincoli sono gli stessi che il backend applica di nuovo per conto suo. */
    const motivoBlocco =
        !carrello.length ? "Aggiungi almeno una tratta al percorso"
            : carrello.length > MAX_TRATTE_PERCORSO ? `Un percorso puo' contenere al massimo ${MAX_TRATTE_PERCORSO} tratte`
                : !dataRecupero ? "Scegli la data del giro"
                    : !operatori.length ? "Scegli almeno un operatore"
                        : null;

    /**
     * Crea il percorso di recupero su Interacta (community 122).
     * Si manda solo cio' che l'utente ha scelto: il backend ricava l'itinerario dalla
     * data e tiene il giro fuori dalla reportistica. L'ordine del carrello e' quello
     * in cui l'operatore trovera' le tratte sull'app, quindi si spedisce com'e'.
     */
    const creaPercorso = async () => {
        if (creando || motivoBlocco) return;
        setCreando(true);
        setErroreCrea(null);
        try {
            const res = await axios.post(
                `${API_URL}/gestione-percorsi/percorsi/recupero`,
                {
                    nome: nomeRecupero.trim() || NOME_RECUPERO,
                    data: dataRecupero,
                    assegnatariIds: operatori.map((o) => o.value),
                    trattoPostIds: carrello.map((c) => c.id),
                },
                { headers: authHeaders() }
            );
            setCreato(res.data);
        } catch (e) {
            console.error("Errore creazione percorso di recupero:", e);
            const payload = e?.response?.data;
            setErroreCrea(payload?.error || "Non e' stato possibile creare il percorso di recupero.");
        } finally {
            setCreando(false);
        }
    };

    /* Export Excel. Manda al backend le righe GIA' filtrate: il file esporta
       esattamente cio' che si sta guardando, e il backend non deve reimplementare
       la logica di filtro della pagina. */
    const esporta = async () => {
        try {
            const res = await axios.post(
                `${API_URL}/gestione-percorsi/report/export`,
                { tipo: "recuperi", periodo: `al ${formatData(meta.aggiornatoAl)}`, giorni: [], righe: recuperiFiltrati },
                { headers: authHeaders(), responseType: "blob" }
            );
            const nome = res.headers["content-disposition"]?.match(/filename=([^;]+)/)?.[1]?.trim();
            saveAs(res.data, nome || "tratte_da_recuperare.xlsx");
        } catch (e) {
            console.error("Errore export Excel:", e);
            alert("Non è stato possibile generare il file Excel.");
        }
    };

    return (
        <>
            {/* Loader a tutto schermo durante la scrittura su Interacta: sono due chiamate
                in fila (create-post + edit del deep-link), quindi l'attesa si vede. Stesso
                overlay del "Genera percorso operativo" in Anagrafica Percorsi. */}
            {creando && (
                <div className="gp-save-overlay gp-overlay-top">
                    <div className="gp-save-card">
                        <Lf23Loader testo="Creazione del percorso di recupero..." />
                    </div>
                </div>
            )}

            <AvvisoEsecuzioni meta={meta} testoDanno={
                "Senza di esse ogni tratta risulta non eseguita, quindi questo elenco contiene l’intera rete e non è attendibile."
            } />

            <div className="gp-toolbar-row">
                <button className={`toggle-filters-btn ${showFilters ? "active" : ""}`} onClick={() => setShowFilters((v) => !v)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                    {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                    {nFiltri > 0 && <span className="gp-rep-nfiltri">{nFiltri}</span>}
                    <svg className="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                </button>

                {showFilters && (
                    <div className="filters-panel">
                        <div className="filters-grid">
                            <div className="filter-group">
                                <label>Via o tratto</label>
                                <input type="text" placeholder="Cerca..." value={fVia}
                                    onChange={(e) => setFVia(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") applicaFiltri(); }} />
                            </div>
                            {conQuartieri && (
                                <div className="filter-group">
                                    <label>Quartiere</label>
                                    <FiltroMulti placeholder="Tutti" options={opzioni.quartieri} value={fQuartieri} onChange={setFQuartieri} />
                                </div>
                            )}
                            <div className="filter-group">
                                <label>Percorso</label>
                                <FiltroMulti placeholder="Tutti" options={opzioni.percorsi} value={fPercorsi} onChange={setFPercorsi} />
                            </div>
                            <div className="filter-group">
                                <label>Priorità</label>
                                <FiltroMulti placeholder="Tutte" options={opzioni.livelli} value={fLivelli} onChange={setFLivelli} />
                            </div>
                            <div className="filter-group">
                                <label>Passaggi/anno</label>
                                <FiltroMulti placeholder="Tutti" options={opzioni.pas} value={fPas} onChange={setFPas} />
                            </div>
                        </div>
                        <div className="filter-actions">
                            <button className="btn btn-ghost" onClick={resetFiltri}>Reset</button>
                            <button className="btn btn-primary" onClick={applicaFiltri}>Applica</button>
                        </div>
                    </div>
                )}
            </div>

            <div className="stats-grid gp-rep-kpi">
                {/* stella piena: la stessa icona che segna le tratte da recuperare nel tab 1 */}
                <KpiCard tone="red" label="Tratte da recuperare" valore={nf(totali)}
                    sub={`al ${formatData(meta.aggiornatoAl)}`}
                    titolo={`Tratte previste e non eseguite all’ultimo passaggio utile, sui ${nf(meta.percorsiConsiderati)} percorsi conclusi.`}
                    icona={(
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                    )} />
                <KpiCard tone="amber" label="Urgenti" valore={nf(rosse)}
                    sub={`${meta.sogliaRossa || 2} o più mancate di fila`}
                    titolo="Tratte con stella rossa: saltate almeno due volte consecutive."
                    icona={ico(<><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>)} />
                <KpiCard tone="blue" label="Nel percorso di recupero"
                    valore={`${nf(kmCarrello, 1)} km`}
                    sub={`${carrello.length} tratt${carrello.length === 1 ? "a" : "e"}`}
                    titolo="Quello che hai raccolto finora con il pulsante +."
                    icona={ico(<path d="M4 19l4-14M20 19l-4-14M12 5v3m0 4v3m0 4v2" />)} />
            </div>

            <div className="gp-view-bar">
                <div className="view-toggle-compact" role="group" aria-label="Vista delle tratte da recuperare">
                    <button className={`view-btn-compact ${vista === "tabella" ? "active" : ""}`} onClick={() => setVista("tabella")} aria-pressed={vista === "tabella"}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9h18M3 15h18M9 3v18" /></svg>
                        Tabella
                    </button>
                    <button className={`view-btn-compact ${vista === "mappa" ? "active" : ""}`} onClick={() => setVista("mappa")} aria-pressed={vista === "mappa"}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 20l-5.5-2.5V4L9 6.5m0 13.5l6-3m-6 3V6.5m6 10.5l5.5 2.5V6L15 3.5m0 13.5V3.5" /></svg>
                        Mappa
                    </button>
                </div>
            </div>

            <div className={`table-container gp-rep-table ${fsRecuperi ? "gp-fullscreen-table" : ""}`}>
                <RotateDevice active={fsRecuperi} maxWidth={1024} />
                <div className="gp-table-topbar gp-rec-topbar">
                    <div style={{ marginRight: "auto", minWidth: 0 }}>
                        <div className="gp-rep-title">Tratte da recuperare</div>
                        <div className="gp-rep-sub">
                            Tratte previste e non eseguite all’ultimo passaggio utile: 1 mancata &rarr; stella gialla, 2 consecutive &rarr; rossa.
                            È uno <strong>stato corrente</strong> (al {formatData(meta.aggiornatoAl)}), non una statistica di periodo.
                        </div>
                    </div>
                    {/* Contatore del carrello: compare solo quando c'e' qualcosa dentro, e apre il modale. */}
                    {carrello.length > 0 && (
                        <button className="results-badge gp-rep-carrello" key={carrello.length}
                            onClick={apriModale} title="Apri il riepilogo del percorso di recupero">
                            {carrello.length} tratt{carrello.length === 1 ? "a selezionata" : "e selezionate"} · {nf(kmCarrello, 1)} km
                        </button>
                    )}
                    <button className="btn btn-ghost gp-new-btn" onClick={onAggiorna} disabled={aggiornando}
                        title="Ricalcola le tratte da recuperare (ignora la cache)">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
                        {aggiornando ? "Aggiornamento…" : "Aggiorna"}
                    </button>
                    <ExportBtn titolo="Esporta le tratte da recuperare in Excel" onExport={esporta} />
                    <FullscreenBtn attivo={fsRecuperi} onClick={() => setFsRecuperi((v) => !v)} />
                </div>
                {/* Stesso riquadro e stessa barra (contatore = finalizza, Aggiorna, Export, Tutto
                    schermo) per le due viste: cambia solo cio' che sta sotto. */}
                {vista === "mappa" ? (
                    <VistaMappaRecuperi righe={recuperiFiltrati} geo={geo} codiciCarrello={codiciCarrello}
                        inCarrello={inCarrello} onPerno={perno} onStorico={setStoricoTarget} espansa={fsRecuperi} />
                ) : (
                <div className="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <ThOrdinabile k="stato" {...intestazione} className="gp-c" title="Ordina per urgenza (colore della stella e mancate di fila)">Stato</ThOrdinabile>
                                <ThOrdinabile k="via" {...intestazione} title="Ordina per via">Via</ThOrdinabile>
                                <ThOrdinabile k="tratto" {...intestazione} title="Ordina per tratto">Tratto</ThOrdinabile>
                                <ThOrdinabile k="percorsi" {...intestazione} title="Ordina per percorso associato">Percorsi associati</ThOrdinabile>
                                <ThOrdinabile k="quartiere" {...intestazione} className="gp-c" title="Ordina per quartiere">Quartiere</ThOrdinabile>
                                <ThOrdinabile k="pas" {...intestazione} className="num gp-c" title="Ordina per passaggi all'anno">Pass./anno</ThOrdinabile>
                                <ThOrdinabile k="metri" {...intestazione} className="num gp-c" title="Ordina per lunghezza">Lunghezza</ThOrdinabile>
                                <ThOrdinabile k="ultima" {...intestazione} className="gp-c" title="Ordina per data dell'ultimo passaggio saltato">Ultimo passaggio saltato</ThOrdinabile>
                                <th className="col-actions">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recuperiFiltrati.map((c) => {
                                const dentro = inCarrello.has(c.id);
                                return (
                                    <tr key={c.id}
                                        className={`${dentro ? "gp-rep-dentro" : ""} ${flashId === c.id ? "gp-rep-flash" : ""}`}>
                                        <td className="gp-c">{stellaRecupero(c)}</td>
                                        <td className="strong gp-rep-via">
                                            {c.via}
                                            {dentro && (
                                                <span className="gp-rep-inclusa" title="Questa tratta è già nel percorso di recupero">
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                    nel recupero
                                                </span>
                                            )}
                                        </td>
                                        <td className="sub">{c.tratto || "—"}</td>
                                        <td>
                                            <div className="gp-percorsi-cell">
                                                {c.percorsi.map((g) => <span className="gp-percorso-pill" key={g}>{g}</span>)}
                                            </div>
                                        </td>
                                        <td className="sub gp-c gp-rep-nowrap">{quartiere(c.quartiere)}</td>
                                        <td className="num gp-c">{c.pas}</td>
                                        <td className="num gp-c gp-rep-nowrap">{nf(c.metri)} m</td>
                                        <td className="gp-c gp-rep-nowrap">{formatData(c.ultima)}</td>
                                        <td className="gp-c gp-rep-nowrap">
                                            {/* Dettaglio: lo storico della tratta nei percorsi (il perche' della stella). */}
                                            <button className="gp-dettaglio-btn" onClick={() => setStoricoTarget(c)}
                                                title="Dettaglio: storico della tratta nei percorsi"
                                                aria-label={`Storico di ${c.via}`}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" /><polyline points="12 16 16 12 12 8" /><line x1="8" y1="12" x2="16" y2="12" />
                                                </svg>
                                            </button>
                                            <button className={`icon-btn ${dentro ? "danger" : "primary"}`} onClick={() => perno(c)}
                                                title={dentro ? "Togli dal percorso di recupero" : "Aggiungi al percorso di recupero"}
                                                aria-label={dentro ? `Togli ${c.via} dal percorso di recupero` : `Aggiungi ${c.via} al percorso di recupero`}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    {!dentro && <line x1="12" y1="5" x2="12" y2="19" />}
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {recuperiFiltrati.length === 0 && (
                                <tr><td colSpan={9}><div className="gp-empty">
                                    {nFiltri > 0
                                        ? "Nessuna tratta corrisponde ai filtri impostati."
                                        : "Nessuna tratta in attesa di recupero: tutte le tratte previste sono state eseguite."}
                                </div></td></tr>
                            )}
                        </tbody>
                        {elenco.length > 0 && (
                            <tfoot>
                                <tr>
                                    <td colSpan={9}>
                                        {nFiltri > 0 ? (
                                            <>Mostrate <strong>{recuperiFiltrati.length}</strong> di {totali} tratte in attesa di recupero.
                                            {sort.key && " Ordinamento personalizzato: la priorità di recupero non è più quella delle prime righe."}</>
                                        ) : (
                                            <><strong>{totali}</strong> tratte in attesa di recupero,
                                            di cui <strong>{rosse}</strong> con due o più mancate consecutive.
                                            {sort.key && " Ordinate su una colonna: le più urgenti non sono più in cima."}</>
                                        )}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
                )}
            </div>

            {/* ------------------- MODALE: STORICO DELLA TRATTA (Dettaglio) ------------------- */}
            {storicoTarget && (
                <ModaleStoricoTratta
                    trattoId={storicoTarget.id}
                    intestazione={{ via: storicoTarget.via, tratto: storicoTarget.tratto, codice: storicoTarget.codice, quartiere: storicoTarget.quartiere }}
                    onClose={() => setStoricoTarget(null)}
                />
            )}

            {/* ------------------- MODALE: CREA PERCORSO DI RECUPERO -------------------
                Ricalca il modale "Genera percorso operativo" di TabAnagraficaPercorsi:
                stessa taglia (.modal wide .gp-modal-full), stessa griglia data+operatori,
                stessa lista riordinabile. La differenza e' che le tratte non si cercano,
                arrivano dal carrello. Si ferma qui: la scrittura su Interacta e' fuori scope. */}
            {/* Il carrello si apre anche dalla tabella (o mappa) a tutto schermo (z 9999): overlay
                sopra (gp-overlay-top), e sopra di lui la tendina operatori (11000) e il loader. */}
            {modaleRecupero && (
                <div className="modal-overlay gp-overlay-top" {...clicFuori(chiudiModale)}>
                    <div className="modal wide gp-modal-full">
                        <div className="modal-header">
                            <div className="m-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="6" cy="19" r="3" />
                                    <circle cx="18" cy="5" r="3" />
                                    <path d="M9 19h5a4 4 0 0 0 0-8h-4a4 4 0 0 1 0-8h5" />
                                </svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <h3>Crea percorso di recupero</h3>
                                <div className="h-sub">Community 122 (Percorsi) — crea il post col link all'app operatori</div>
                            </div>
                            <button className="modal-close" onClick={chiudiModale} aria-label="Chiudi">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        {erroreCrea && <div className="modal-error">{erroreCrea}</div>}

                        <div className="modal-body">
                            {creato ? (
                                /* A cose fatte il modale diventa un riepilogo: il form e il
                                   carrello non servono piu', e il link all'app operatori e'
                                   la cosa per cui si e' li'. Stessa forma del Genera. */
                                <div className="gp-info-section">
                                    <h4>Percorso di recupero creato ✓</h4>
                                    <p>
                                        {creato.nome && <><strong>{creato.nome}</strong> · </>}
                                        customId: <strong>{creato.customId}</strong> · {carrello.length} tratt{carrello.length === 1 ? "a" : "e"} · {nf(kmCarrello, 2)} km
                                    </p>
                                    <p className="muted" style={{ marginTop: 6 }}>
                                        Le tratte restano da recuperare finché il giro non viene eseguito:
                                        la stella si spegne quando l’operatore le fa davvero.
                                    </p>
                                    <label style={{ display: "block", marginTop: 16, fontSize: 12, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: ".5px" }}>
                                        Link app operatori
                                    </label>
                                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                                        <input type="text" readOnly value={creato.url || ""} onFocus={(e) => e.target.select()}
                                            style={{ flex: 1, padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }} />
                                        <button className="btn btn-ghost" onClick={() => { if (navigator.clipboard && creato.url) navigator.clipboard.writeText(creato.url); }}>Copia</button>
                                        <a className="btn btn-ghost" href={creato.url} target="_blank" rel="noopener noreferrer">Apri</a>
                                    </div>
                                </div>
                            ) : (<>
                            <div className="gp-gen-grid">
                                <div className="form-group">
                                    <label htmlFor="gp-rec-data">Data prevista</label>
                                    <DatePickerModal id="gp-rec-data" value={dataRecupero} min={oggi}
                                        onChange={(e) => {
                                            setDataRecupero(e.target.value);
                                            setErroreCrea(null);
                                        }} label="Data prevista" />
                                </div>
                                <div className="form-group">
                                    <label>Operatori</label>
                                    <Select
                                        isMulti
                                        classNamePrefix="rs"
                                        options={utentiOptions}
                                        value={operatori}
                                        onChange={(vals) => setOperatori(vals || [])}
                                        placeholder={utentiLoading ? "Caricamento operatori..." : "Seleziona uno o più operatori"}
                                        isLoading={utentiLoading}
                                        noOptionsMessage={() => "Nessun operatore"}
                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 11000 }) }}
                                    />
                                </div>
                            </div>

                            <div className="form-group" style={{ marginTop: 14 }}>
                                <label htmlFor="gp-rec-nome">Nome del percorso</label>
                                <input id="gp-rec-nome" type="text" value={nomeRecupero} maxLength={80}
                                    onChange={(e) => setNomeRecupero(e.target.value)} placeholder={NOME_RECUPERO} />
                                {progressivo != null && (
                                    <div className="sub" style={{ marginTop: 6 }}>
                                        Il giro si chiamerà <strong>REC{progressivo} {nomeRecupero.trim().replace(/^REC\d+\b\s*/i, "") || NOME_RECUPERO}</strong>
                                        {progressivo > 1 && <> · in questa data ci sono già {progressivo - 1} recuper{progressivo - 1 === 1 ? "o" : "i"}</>}
                                    </div>
                                )}
                            </div>

                            <div className="gp-rep-sel-head">
                                <span className="section-label" style={{ margin: 0 }}>
                                    Tratte del percorso ({qModale ? `${carrelloVisibile.length} di ${carrello.length}` : carrello.length}) — l’ordine è quello del giro
                                </span>
                                <div className="gp-rep-sel-actions">
                                    {/* "Mostra": e' una vista, non toglie niente dal percorso. */}
                                    {conQuartieri && (
                                        <label className="gp-rec-mostra">
                                            Mostra
                                            <select className="gp-rep-qfiltro" value={qModale} onChange={(e) => setQModale(e.target.value)}
                                                disabled={quartieriCarrello.length < 2}
                                                title={quartieriCarrello.length < 2 ? "Tutte le tratte sono dello stesso quartiere" : "Mostra solo le tratte di un quartiere: le altre restano nel percorso"}
                                                aria-label="Mostra solo le tratte di un quartiere (le altre restano nel percorso)">
                                                <option value="">Tutti i quartieri</option>
                                                {quartieriCarrello.map((q) => <option key={q} value={q}>Solo {quartiere(q)}</option>)}
                                            </select>
                                        </label>
                                    )}
                                    {/* Svuota toglie TUTTO il carrello, anche le tratte nascoste dal filtro: col filtro lo si dice. */}
                                    <button className="btn btn-ghost gp-new-btn" onClick={svuota} disabled={!carrello.length}
                                        title={qModale ? `Toglie tutte le ${carrello.length} tratte del percorso, anche quelle non mostrate` : undefined}>
                                        {qModale ? "Svuota tutto" : "Svuota"}
                                    </button>
                                </div>
                            </div>
                            {/* Col filtro attivo va detto chiaro che e' solo una vista: chi non l'ha messo
                                potrebbe credere che le altre tratte siano state tolte dal giro. */}
                            {!riordinabile && (
                                <div className="gp-rec-filtro-attivo">
                                    <span>
                                        <strong>Solo visualizzazione:</strong> vedi {carrelloVisibile.length} delle {carrello.length} tratte
                                        del percorso ({quartiere(qModale)}).
                                        {carrello.length - carrelloVisibile.length === 1
                                            ? " L’altra resta nel giro e viene creata insieme."
                                            : ` Le altre ${carrello.length - carrelloVisibile.length} restano nel giro e vengono create insieme.`}
                                        {" "}Per riordinare servono tutte le tratte.
                                    </span>
                                    <button type="button" onClick={() => setQModale("")}>Mostra tutte</button>
                                </div>
                            )}

                            {/* Elenco riordinabile e, accanto, la mappa del giro coi numeri d'ordine
                                (task 548). La mappa mostra le righe dell'elenco: col filtro quartiere,
                                solo quelle, sempre con la loro posizione reale nel giro. */}
                            <div className={`gp-rec-tratte ${carrello.length ? "con-mappa" : ""}`}>
                            <div className="gp-selected-box gp-rec-box">
                                {carrello.length === 0 ? (
                                    <div className="gp-box-empty">
                                        Nessuna tratta selezionata.<br />
                                        Aggiungile dalla tabella col pulsante <strong>+</strong>.
                                    </div>
                                ) : carrelloVisibile.length === 0 ? (
                                    <div className="gp-box-empty">Nessuna tratta di {quartiere(qModale)} in questo percorso.</div>
                                ) : carrelloVisibile.map(({ c, i }) => (
                                    <div
                                        className={`tratto-row ${c.livello === "rossa" ? "is-recupero-rossa" : ""} ${dragIndex === i ? "gp-dragging" : ""} ${movedId === c.id ? "gp-just-moved" : ""}`}
                                        key={c.id}
                                        data-row
                                        data-tratta-id={c.id}
                                        onDragOver={(e) => { if (!riordinabile || dragIndex === null) return; e.preventDefault(); if (dragIndex !== i) { spostaTratta(dragIndex, i); setDragIndex(i); } }}
                                        onDrop={(e) => e.preventDefault()}
                                    >
                                        {/* Desktop (>=1025px): maniglia con drag nativo, l'immagine trascinata e' la riga intera. */}
                                        <span
                                            className="gp-drag-handle"
                                            draggable={riordinabile}
                                            onDragStart={(e) => {
                                                const row = e.currentTarget.closest("[data-row]");
                                                const rect = row.getBoundingClientRect();
                                                e.dataTransfer.effectAllowed = "move";
                                                try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) { /* Safari */ }
                                                e.dataTransfer.setDragImage(row, e.clientX - rect.left, e.clientY - rect.top);
                                                setDragIndex(i);
                                            }}
                                            onDragEnd={() => setDragIndex(null)}
                                            style={riordinabile ? undefined : { opacity: .3, cursor: "default" }}
                                            title={riordinabile ? "Trascina per riordinare" : "Riordino disponibile solo senza filtro"}
                                            aria-label={`Trascina per riordinare ${c.via}`}
                                            role="button"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
                                        </span>
                                        {/* Mobile/tablet (<=1024px): frecce su/giu'. */}
                                        <div className="gp-move-col">
                                            <button className="gp-move-btn" title="Sposta su" disabled={!riordinabile || i === 0}
                                                onClick={() => { spostaTratta(i, i - 1); flashMoved(c.id); }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6" /></svg>
                                            </button>
                                            <button className="gp-move-btn" title="Sposta giù" disabled={!riordinabile || i === carrello.length - 1}
                                                onClick={() => { spostaTratta(i, i + 1); flashMoved(c.id); }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
                                            </button>
                                        </div>
                                        {/* Numerino d'ordine editabile: scrivi la posizione e premi Invio. Esc annulla. */}
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className="idx idx-input"
                                            value={idxEditId === c.id ? idxEditVal : i + 1}
                                            readOnly={!riordinabile}
                                            title={riordinabile ? "Scrivi la posizione e premi Invio" : `Posizione ${i + 1} nel giro`}
                                            aria-label={`Posizione di ${c.via}: ${i + 1}`}
                                            onFocus={(e) => { setIdxEditId(c.id); setIdxEditVal(String(i + 1)); e.target.select(); }}
                                            onChange={(e) => setIdxEditVal(e.target.value.replace(/\D/g, "").slice(0, 3))}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") e.currentTarget.blur();
                                                if (e.key === "Escape") { setIdxEditVal(String(i + 1)); e.currentTarget.blur(); }
                                            }}
                                            onBlur={() => applyIdxEdit(i)}
                                        />
                                        {stellaRecupero(c)}
                                        <div className="t-name">
                                            <b>{c.via}</b>
                                            <span>{c.tratto || "—"}{c.quartiere && ` · ${quartiere(c.quartiere)}`}<span className="gp-pass-badge">{c.pas} pass./anno</span></span>
                                        </div>
                                        <span className="gp-rep-sel-km">{nf(km(c.metri), 2)} km</span>
                                        <button className="icon-btn danger" onClick={() => perno(c)}
                                            title="Togli dal percorso" aria-label={`Togli ${c.via}`}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {carrello.length > 0 && <MappaCarrello voci={carrelloVisibile} geo={geo} onScegli={scegliDaMappa} filtro={qModale ? quartiere(qModale) : null} />}
                            </div>

                            {carrello.length > 0 && kmCarrello < MIN_KM_RECUPERO && (
                                <div className="gp-rep-warn">
                                    Il percorso misura {nf(kmCarrello, 2)} km, sotto il minimo di {MIN_KM_RECUPERO} km.
                                    <em> Soglia provvisoria, da confermare.</em>
                                </div>
                            )}

                            {carrello.length > MAX_TRATTE_PERCORSO && (
                                <div className="gp-rep-warn">
                                    Il percorso ha {carrello.length} tratte, ma su Interacta un percorso ne contiene
                                    al massimo {MAX_TRATTE_PERCORSO}. Togline {carrello.length - MAX_TRATTE_PERCORSO} oppure crea due giri.
                                </div>
                            )}

                            </>)}
                        </div>

                        <div className="modal-footer">
                            <div className="footer-hint">
                                {carrello.length} tratt{carrello.length === 1 ? "a" : "e"} · <strong>{nf(kmCarrello, 2)} km</strong> in totale
                            </div>
                            <div className="footer-actions">
                                {creato ? (
                                    <button className="btn btn-primary" onClick={chiudiModale}>Chiudi</button>
                                ) : (
                                    <>
                                        <button className="btn btn-ghost" onClick={chiudiModale} disabled={creando}>Annulla</button>
                                        <button className="btn btn-primary"
                                            onClick={() => creaPercorso()}
                                            disabled={creando || !!motivoBlocco}
                                            title={motivoBlocco || undefined}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            {creando ? "Creazione…" : "Crea percorso"}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

/**
 * Il guscio: carica l'elenco e decide cosa mostrare prima che i dati ci siano.
 * Il tab e' montato solo quando e' selezionato, quindi la chiamata parte all'apertura.
 */
const TabRecuperi = ({ zona }) => {
    const [dati, setDati] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errore, setErrore] = useState(null);

    const carica = useCallback(async ({ refresh = false } = {}) => {
        setLoading(true);
        setErrore(null);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/recuperi`, {
                headers: authHeaders(),
                params: { zona, refresh: refresh ? 1 : undefined },
            });
            setDati(res.data);
        } catch (e) {
            console.error("Errore caricamento tratte da recuperare:", e);
            setErrore(e?.response?.data?.error || "Non è stato possibile caricare le tratte da recuperare.");
            setDati(null);
        } finally {
            setLoading(false);
        }
    }, [zona]);

    /* Aggiornamento "morbido": `carica` accende `loading`, che sostituisce la pagina
       col loader e smonta il corpo — portandosi via il carrello che l'utente sta
       componendo. Qui si aggiorna solo il payload e il corpo resta in piedi. */
    const [aggiornando, setAggiornando] = useState(false);
    const aggiorna = useCallback(async () => {
        setAggiornando(true);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/recuperi`, {
                headers: authHeaders(),
                params: { zona, refresh: 1 },
            });
            setDati(res.data);
        } catch (e) {
            console.error("Errore aggiornamento tratte da recuperare:", e);
        } finally {
            setAggiornando(false);
        }
    }, [zona]);

    useEffect(() => { carica(); }, [carica]);

    if (loading) {
        return (
            <div className="gp-loading">
                <Lf23Loader testo="Calcolo delle tratte da recuperare..." />
            </div>
        );
    }

    if (errore) {
        return (
            <div className="gp-empty">
                <p>{errore}</p>
                <button className="btn btn-ghost" onClick={() => carica()}>Riprova</button>
            </div>
        );
    }

    if (!dati) return null;
    return <Recuperi dati={dati} onAggiorna={aggiorna} aggiornando={aggiornando} zona={zona} />;
};

export default TabRecuperi;
